import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DatePickerModule } from 'primeng/datepicker';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SelectModule } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectButtonModule } from 'primeng/selectbutton';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { GoogleAnalyticsService } from 'ngx-google-analytics';
import { catchError, debounceTime, finalize, forkJoin, of } from 'rxjs';
import { DeliveryCashBoxOption, PettyCashService, preferredDeliveryCashBoxId } from '../../accounts/workforce-cash/petty-cash.service';
import { WorkforceCashService } from '../../accounts/workforce-cash/workforce-cash.service';
import { Company } from '../../company/company.model';
import { CompanyService } from '../../company/company.service';
import { CrmService } from '../../crm/crm.service';
import { CrmClient } from '../../crm/crm.model';
import { ContractsService } from '../../contracts/contracts.service';
import { ContractPricingDecisionResponse } from '../../contracts/models/contract-pricing-decision.model';
import { OrdersService } from '../../order/order.service';
import { PaymentTerm } from '../../order/models/payment-term.model';
import { Customer } from '../../customer/customer.model';
import { Driver } from '../../fleet/driver/driver.model';
import { DriverService } from '../../fleet/driver/driver.service';
import { Vehicle } from '../../fleet/vehicle/vehicle.model';
import { VehicleService } from '../../fleet/vehicle/vehicle.service';
import { VehicleType } from '../../fleet/vehicle/params/vehicle-type';
import { Location } from '../../location/location.model';
import { LocationService } from '../../location/location.service';
import { LazyLocationPickerDirective } from '../../shared/location-picker/lazy-location-picker.directive';
import { City } from '../../models/common/city.model';
import { Country } from '../../models/common/country.model';
import { PayloadType } from '../../models/meta-params/payload-type';
import { UOM } from '../../models/meta-params/uom';
import { Weight } from '../../models/meta-params/weight';
import { PartyService } from '../../party/party.service';
import { PartyType } from '../../party/party-type.enum';
import { BaseMasterDataService } from '../../services/base-master-data.service';
import { SessionStorageService } from '../../services/session-storage.service';
import { CommercialPartyPickerComponent } from '../../shared/commercial-party-picker/commercial-party-picker.component';
import { CommercialPartyReference } from '../../shared/commercial-party-picker/commercial-party.model';
import { CountryCurrencySelectComponent } from '../../shared/country-currency-select/country-currency-select.component';
import { InternationalPhoneFieldComponent } from '../../shared/international-phone-field/international-phone-field.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { LocationSearchComponent } from '../../shared/location-search/location-search.component';
import { FieldWorkService } from '../../field-work/field-work.service';
import { FieldLaborRoleOption, FieldWorker } from '../../field-work/field-work.models';
import { ShipmentIntakeLaborRequirement, ShipmentIntakeRequest, ShipmentIntakeWarehouseItemOption } from '../models/intake/shipment-intake.model';
import {
    CreatedEntityInfo,
    DeliveryLaborSelection,
    ExpressLocationContactSelection,
    ExpressLocationStop,
    InlineClientDraft,
    InlineContactDraft,
    InlineLaborerDraft,
    InlineLocationDraft,
    ShipmentQuickCreateRequest,
    ShipmentQuickCreateResponse
} from '../simple-operations/shipment-experience.model';
import { ShipmentExperienceService } from '../simple-operations/shipment-experience.service';
import { ShipmentIntakeService } from '../shipment-intake.service';
import { WarehousingService } from '../../warehousing/warehousing.service';
import { WarehousingMasterData } from '../../warehousing/warehousing.model';
import { warehousePickupIssue } from '../warehouse-pickup';

interface ModeOption {
    label: string;
    value: 'existing' | 'new';
    icon: string;
}

type ResourceOwnershipType = 'OWNED' | 'FREELANCE' | 'RENTAL' | 'TRANSPORTER';

@Component({
    selector: 'app-express-shipment',
    standalone: true,
    templateUrl: './express-shipment.component.html',
    styleUrls: ['./express-shipment.component.css'],
    imports: [
        CommonModule,
        ReactiveFormsModule,
        ButtonModule,
        CheckboxModule,
        DatePickerModule,
        IftaLabelModule,
        InputGroupModule,
        InputGroupAddonModule,
        InputNumberModule,
        InputTextModule,
        ProgressSpinnerModule,
        SelectModule,
        MultiSelectModule,
        LazyLocationPickerDirective,
        SelectButtonModule,
        TextareaModule,
        ToastModule,
        PageHeroComponent,
        PageShellComponent,
        CommercialPartyPickerComponent,
        CountryCurrencySelectComponent,
        InternationalPhoneFieldComponent,
        LocationSearchComponent
    ],
    providers: [MessageService]
})
export class ExpressShipmentComponent implements OnInit {
    cashBoxOptions: DeliveryCashBoxOption[] = [];
    cashBoxError = '';
    availableCashBoxes(currencyCode?: string): DeliveryCashBoxOption[] {
        return this.cashBoxOptions.filter(box => box.currencyCode === currencyCode);
    }
    readonly cashPurposeOptions = [
        { label: 'No cash', value: 'NONE' },
        { label: 'Company expense float', value: 'COMPANY_EXPENSE_FLOAT' },
        { label: 'Advance against pay', value: 'ADVANCE_AGAINST_PAY' }
    ];
    readonly driverPayMethods = [{ label: 'No driver pay', value: 'NONE' }, { label: 'Fixed trip', value: 'FIXED_TRIP' }, { label: 'Origin to destination', value: 'CITY_PAIR_TRIP' }, { label: 'Per kilometre', value: 'PER_KM' }];
    readonly payDistanceSources = [{ label: 'Shipment route distance', value: 'PLANNED_ROUTE' }, { label: 'GPS device odometer', value: 'GPS_ODOMETER' }, { label: 'Start/end odometer photos', value: 'PHOTO_ODOMETER' }];

    readonly modeOptions: ModeOption[] = [
        { label: 'Existing', value: 'existing', icon: 'pi pi-search' },
        { label: 'New', value: 'new', icon: 'pi pi-plus' }
    ];
    readonly locationTypes = [
        { name: 'Customer Site', value: 'CUSTOMER' },
        { name: 'Warehouse', value: 'WAREHOUSE' },
        { name: 'Distribution Center', value: 'DISTRIBUTION_CENTER' },
        { name: 'Cross Dock', value: 'CROSS_DOCK' },
        { name: 'Retail Store', value: 'STORE' },
        { name: 'Office', value: 'OFFICE' },
        { name: 'Branch', value: 'BRANCH' },
        { name: 'Depot', value: 'DEPOT' },
        { name: 'Yard', value: 'YARD' },
        { name: 'Hub', value: 'HUB' },
        { name: 'Factory / Plant', value: 'FACTORY' },
        { name: 'Supplier / Vendor Site', value: 'SUPPLIER' },
        { name: 'Port / Terminal', value: 'PORT' },
        { name: 'Service Center', value: 'SERVICE_CENTER' },
        { name: 'Temporary Site', value: 'TEMPORARY_SITE' }
    ];
    readonly resourceOwnershipOptions = [
        { label: 'Owned', value: 'OWNED' as ResourceOwnershipType },
        { label: 'Freelance', value: 'FREELANCE' as ResourceOwnershipType },
        { label: 'Rental', value: 'RENTAL' as ResourceOwnershipType },
        { label: 'Transporter', value: 'TRANSPORTER' as ResourceOwnershipType }
    ];
    readonly cargoSourceOptions = [
        { label: 'General cargo', value: 'GENERAL_CARGO' },
        { label: 'Warehouse item', value: 'WAREHOUSE_ITEM' },
        { label: 'Container', value: 'CONTAINER' }
    ];
    readonly sellingPriceSourceOptions = [
        { label: 'Manual', value: 'MANUAL' },
        { label: 'Rate card', value: 'RATE_CARD' }
    ];
    readonly workforceBillingUnits = ['HOUR', 'DAY', 'TASK', 'FLAT'];
    readonly containerEquipmentTypeOptions = [
        { label: '20GP - 20 ft General Purpose', value: '20GP' },
        { label: '40GP - 40 ft General Purpose', value: '40GP' },
        { label: '40HC - 40 ft High Cube', value: '40HC' },
        { label: '45HC - 45 ft High Cube', value: '45HC' },
        { label: '20RF - 20 ft Refrigerated', value: '20RF' },
        { label: '40RF - 40 ft Refrigerated', value: '40RF' }
    ];

    form: FormGroup;
    loading = true;
    saving = false;
    fieldIssues: Record<string, string> = {};
    submissionFeedback: { tone: 'error' | 'warning'; title: string; issues: string[] } | null = null;
    existingShipmentId?: number;
    pricingMatch?: ContractPricingDecisionResponse;
    pricingMatchLoading = false;
    pricingMatchUnavailable = '';
    private pricingMatchKey = '';
    private pricingMatchSequence = 0;

    commercialPartyReference: CommercialPartyReference = { source: 'CRM' };
    selectedCrmClient?: CrmClient;
    companies: Company[] = [];
    partyContacts: Customer[] = [];
    drivers: Driver[] = [];
    vehicles: Vehicle[] = [];
    countries: Country[] = [];
    vehicleTypes: VehicleType[] = [];
    payloadTypes: PayloadType[] = [];
    uoms: UOM[] = [];
    weightUnits: Weight[] = [];
    warehouseItems: ShipmentIntakeWarehouseItemOption[] = [];
    warehouseOptions: WarehousingMasterData[] = [];
    readonly warehouseFlowOptions = [
        { label: 'Outbound from warehouse', value: 'OUTBOUND' },
        { label: 'Inbound to warehouse', value: 'INBOUND' }
    ];
    outboundPickupIssue(): string {
        const value = this.form?.getRawValue();
        if (value?.cargoSource !== 'WAREHOUSE_ITEM' || value.warehouseFlowDirection === 'INBOUND'
            || !value.warehouseItem?.inventoryItemId) return '';
        return warehousePickupIssue(value.warehouseItem.itemName || `Item ${value.warehouseItem.inventoryItemId}`,
            value.sourceWarehouseId, value.sourceMode === 'existing' ? value.sourceLocation?.id : null,
            this.warehouseOptions);
    }
    inboundDestinationWarehouse(): WarehousingMasterData | undefined {
        const locationId = this.form.get('destinationLocation')?.value?.id;
        if (this.form.get('destinationMode')?.value !== 'existing' || !locationId) return undefined;
        const matches = this.warehouseOptions.filter(warehouse => warehouse.locationId === locationId
            && warehouse.warehouseOwnershipType === 'COMPANY' && warehouse.isActive !== false);
        return matches.length === 1 ? matches[0] : undefined;
    }
	paymentTerms: PaymentTerm[] = [];
    warehouseItemsLoading = false;
    transporters: CrmClient[] = [];
    workforceRoles: FieldLaborRoleOption[] = [];
    fieldWorkers: FieldWorker[] = [];
    citiesByContext: Record<string, City[]> = {};
    hasTransportationAgency = true;

    private billingCustomerId?: number;
    private commercialPartyLegacyCustomerId?: number;
    private submissionKey?: string;
    private completed = false;
    private readonly openedAt = Date.now();

    constructor(
        private fb: FormBuilder,
        private router: Router,
        private experienceService: ShipmentExperienceService,
        private sessionStorage: SessionStorageService,
        private companyService: CompanyService,
        private partyService: PartyService,
        private crmService: CrmService,
		private ordersService: OrdersService,
        private locationService: LocationService,
        private driverService: DriverService,
        private vehicleService: VehicleService,
        private fieldWorkService: FieldWorkService,
        private shipmentIntakeService: ShipmentIntakeService,
        private warehousingService: WarehousingService,
        private contractsService: ContractsService,
        private baseMasterData: BaseMasterDataService,
        private analytics: GoogleAnalyticsService,
        private messages: MessageService,
        private pettyCash: PettyCashService,
        private workforceCash: WorkforceCashService
    ) {
        this.form = this.fb.group({
            partyMode: ['existing' as 'existing' | 'new'],
            company: [null],
            newClient: this.newClientGroup(),
            addContactForClient: [false],
            contactForClient: this.fb.group({
                name: [''],
                email: [''],
                phone: ['']
            }),

            sourceMode: ['existing' as 'existing' | 'new'],
            sourceLocation: [null],
            newSourceLocation: this.newLocationGroup(),
            sourceContactMode: ['existing' as 'existing' | 'new'],
            sourceContact: [null],
            newSourceContact: this.newContactGroup(),
            destinationMode: ['existing' as 'existing' | 'new'],
            destinationLocation: [null],
            newDestinationLocation: this.newLocationGroup(),
            destinationContactMode: ['existing' as 'existing' | 'new'],
            destinationContact: [null],
            newDestinationContact: this.newContactGroup(),
            intermediateStops: new FormArray<FormGroup>([]),

            externalReference: [''],
            pickupWindow: [null, Validators.required],
            cargoType: [null, Validators.required],
            cargoDescription: [''],
            quantity: [1, [Validators.required, Validators.min(1)]],
            uom: [null],
            totalWeight: [null],
            weightUnit: [null],
            cargoSource: ['GENERAL_CARGO'],
            warehouseItem: [null],
            sourceWarehouseId: [null],
            warehouseFlowDirection: ['OUTBOUND'],
            containerLines: this.fb.array([this.newContainerLine()]),

            sellingPriceSource: ['RATE_CARD'],
            sellingPrice: [0, [Validators.required, Validators.min(0)]],
            currencyCode: ['SAR'],
			paymentTermId: [null, Validators.required],

            truckCount: [1],
            workforceRequirements: new FormArray<FormGroup>([]),
            assignments: new FormArray<FormGroup>([this.newAssignmentRow()]),
            notes: ['']
        });
        this.form.get('sourceLocation')?.valueChanges.subscribe(() => this.prefillCashBoxes());
        this.form.get('warehouseItem')?.valueChanges.subscribe(item =>
            this.form.get('sourceWarehouseId')?.setValue(item?.defaultWarehouseId ?? null));
        for (const name of ['sourceLocation', 'destinationLocation', 'newSourceLocation.city', 'newDestinationLocation.city'])
            this.form.get(name)?.valueChanges.subscribe(() => this.assignmentRows.forEach(row => this.quoteDriverPay(row)));
        this.form.valueChanges.pipe(debounceTime(250)).subscribe(() => this.refreshPricingMatch());
    }

    ngOnInit(): void {
        const company = this.sessionStorage.getCompany();
        this.payloadTypes = this.sessionStorage.getPayloadTypes() ?? [];
        this.uoms = this.sessionStorage.getUOMList() ?? [];
        this.weightUnits = this.sessionStorage.getWeightUnits() ?? [];
        this.countries = this.sessionStorage.getCountryList() ?? [];
        this.vehicleTypes = this.sessionStorage.getVehicleTypes() ?? [];
        this.form.patchValue({
            uom: this.uoms[0] ?? null,
            weightUnit: this.weightUnits[0] ?? null
        }, { emitEvent: false });
        if (!company?.id) {
            this.loading = false;
            this.fieldIssues['form'] = 'Your user is not linked to a company.';
            this.showSubmissionFeedback('error', 'Express shipment is unavailable', [this.fieldIssues['form']]);
            return;
        }

        this.seedCountryDefaults();
        // The company in session storage can be the lightweight login payload and
        // may not include its transportation agency. Refresh it before loading
        // owned crew so a valid company setting is never mistaken for a missing one.
        this.companyService.getCompanyById(company.id).subscribe({
            next: response => {
                const refreshedCompany = response?.data ?? company;
                if (response?.data) {
                    this.sessionStorage.saveCompany(refreshedCompany);
                }
                this.loadWorkspace(refreshedCompany);
            },
            error: () => this.loadWorkspace(company)
        });
    }

    private loadWorkspace(company: Company): void {
        const agencyId = company.transportationAgencyId
            ?? this.sessionStorage.getTransportationAgency()?.rowId;
        this.hasTransportationAgency = !!agencyId;
        forkJoin({
            parties: this.partyService.getParties(company.id, ['Company'], '', 0, 1000),
            partyContacts: this.partyService.getParties(company.id, [PartyType.Customer], '', 0, 1000),
            ownedDrivers: agencyId
                ? this.driverService.findDriversByTransportationAgencyId(0, 250, agencyId)
                : of({ items: [], hasMore: false } as any),
            freelanceDrivers: this.driverService.getFreelanceDriversByCompanyId(0, 250, company.id),
            rentalDrivers: this.driverService.getRentalDriversByCompanyId(0, 250, company.id),
            ownedVehicles: agencyId
                ? this.vehicleService.findVehiclesByTransportationAgencyId(0, 250, agencyId)
                : of({ items: [], hasMore: false } as any),
            freelanceVehicles: this.vehicleService.getFreelanceVehiclesByCompanyId(0, 250, company.id),
            rentalVehicles: this.vehicleService.getRentalVehiclesByCompanyId(0, 250, company.id),
            transporters: this.crmService.getClients(company.id, ['TRANSPORTER']),
			paymentTerms: this.ordersService.getAllPaymentTerms(company.id),
            workforceRoles: this.fieldWorkService.laborRoles(company.id),
            fieldWorkers: this.fieldWorkService.people({ owningCompanyId: company.id, page: 0, pageSize: 200 }),
            cashBoxes: this.pettyCash.deliveryBoxOptions(company.id).pipe(catchError(() => {
                this.cashBoxError = 'Cash boxes could not be loaded. Retry before requesting driver cash.';
                return of([] as DeliveryCashBoxOption[]);
            }))
        }).pipe(finalize(() => this.loading = false)).subscribe({
            next: result => {
                this.companies = result.parties.content.map(party => this.partyService.convertPartyToCompany(party));
                this.partyContacts = (result.partyContacts.content ?? [])
                    .map(party => this.partyService.convertPartyToCustomer(party));
                this.drivers = this.mergeById<Driver>(
                    result.ownedDrivers.items ?? [], result.freelanceDrivers.items ?? [], result.rentalDrivers.items ?? []);
                this.vehicles = this.mergeById<Vehicle>(
                    result.ownedVehicles.items ?? [], result.freelanceVehicles.items ?? [], result.rentalVehicles.items ?? []);
                this.transporters = result.transporters ?? [];
				this.paymentTerms = result.paymentTerms ?? [];
				this.form.patchValue({ paymentTermId: this.paymentTerms[0]?.paymentTermID ?? null }, { emitEvent: false });
                this.workforceRoles = result.workforceRoles ?? [];
                this.fieldWorkers = result.fieldWorkers?.content ?? [];
                this.cashBoxOptions = result.cashBoxes;
                this.prefillCashBoxes();
                this.loadTransporterResources(company.id, this.transporters);
                this.loadWarehouseItems();
                this.warehousingService.getMasterData('WAREHOUSE', { activeStatus: 'ACTIVE' }).subscribe({
                    next: response => this.warehouseOptions = response.data || [],
                    error: () => this.warehouseOptions = []
                });
                this.track('shipment_express_open', {});
            },
            error: error => {
                this.fieldIssues['form'] = error?.error?.message || 'The express shipment workspace could not be loaded.';
                this.showSubmissionFeedback('error', 'Express shipment is unavailable', [this.fieldIssues['form']]);
            }
        });
    }

    get owningCompanyId(): number | undefined { return this.sessionStorage.getCompany()?.id; }

    get assignmentsArray(): FormArray { return this.form.get('assignments') as FormArray; }

    get assignmentRows(): FormGroup[] { return this.assignmentsArray.controls as FormGroup[]; }

    get intermediateStopsArray(): FormArray { return this.form.get('intermediateStops') as FormArray; }

    get intermediateStopRows(): FormGroup[] { return this.intermediateStopsArray.controls as FormGroup[]; }

    get workforceRequirementsArray(): FormArray { return this.form.get('workforceRequirements') as FormArray; }

    get workforceRequirementRows(): FormGroup[] { return this.workforceRequirementsArray.controls as FormGroup[]; }

    get isExistingParty(): boolean { return this.form.get('partyMode')?.value === 'existing'; }

    get canAddContactForClient(): boolean {
        return this.isExistingParty && !!this.commercialPartyReference.crmClientId;
    }

    countryChange(context: string, country: Country | null): void {
        this.citiesByContext[context] = [];
        if (!country?.id) {
            return;
        }
        this.baseMasterData.getCities('en-US', country.id).subscribe({
            next: cities => this.citiesByContext[context] = cities ?? [],
            error: () => this.citiesByContext[context] = []
        });
    }

    citiesFor(context: string): City[] { return this.citiesByContext[context] ?? []; }

    hasAssignmentIssue(): boolean {
        for (let i = 0; i < this.assignmentRows.length; i++) {
            if (this.fieldIssues[`assignment-${i}`]) {
                return true;
            }
        }
        return false;
    }

    onTruckCountChange(value: number | string | null): void {
        const parsed = typeof value === 'number' ? value : Number(value);
        this.syncAssignmentRows(Number.isFinite(parsed) ? parsed : 1);
    }

    removeAssignmentRow(index: number): void {
        if (this.assignmentsArray.length <= 1) {
            return;
        }
        this.assignmentsArray.removeAt(index);
        this.form.get('truckCount')?.setValue(this.assignmentsArray.length, { emitEvent: false });
    }

    addWorkforceRequirement(): void {
        this.workforceRequirementsArray.push(this.newWorkforceRequirement());
    }

    removeWorkforceRequirement(index: number): void {
        this.workforceRequirementsArray.removeAt(index);
        this.syncWorkforceSlots();
        this.prefillCashBoxes();
    }

    private prefillCashBoxes(): void {
        const pickupId = this.form?.get('sourceLocation')?.value?.id;
        const currency = this.form?.get('currencyCode')?.value || 'SAR';
        const preferred = preferredDeliveryCashBoxId(this.cashBoxOptions, pickupId, currency);
        if (!preferred) return;
        this.assignmentRows.forEach(row => {
            const box = row.get('tripMoney.pettyCashBoxId');
            if (box && !box.value) box.setValue(preferred);
        });
    }

    syncWorkforceSlots(): void {
        const definitions = this.workforceSlotDefinitions();
        this.assignmentRows.forEach(row => {
            const current = this.laborSelectionsArray(row);
            const existing = new Map<string, FormGroup>();
            current.controls.forEach(control => {
                const slot = control as FormGroup;
                existing.set(this.workforceSlotKey(slot.get('fieldRoleId')?.value, slot.get('slotNumber')?.value), slot);
            });
            row.setControl('laborSelections', new FormArray<FormGroup>(definitions.map(definition =>
                existing.get(this.workforceSlotKey(definition.fieldRoleId, definition.slotNumber))
                    ?? this.newLaborSelection(definition.fieldRoleId, definition.slotNumber))));
        });
    }

    laborSelectionsArray(row: FormGroup): FormArray {
        return row.get('laborSelections') as FormArray;
    }

    workforceRoleName(roleId?: number): string {
        return this.workforceRoles.find(role => role.fieldRoleId === roleId)?.roleName || 'Labor';
    }

    workforceFieldKey(assignmentIndex: number, slotIndex: number): string {
        return `workforce-${assignmentIndex}-${slotIndex}`;
    }

    availableWorkersForSlot(row: FormGroup, slotIndex: number): FieldWorker[] {
        const selections = this.laborSelectionsArray(row);
        const current = selections.at(slotIndex)?.getRawValue();
        const selectedElsewhere = new Set(selections.controls
            .filter((_, index) => index !== slotIndex)
            .map(control => control.get('fieldWorker')?.value?.fieldWorkerId)
            .filter((id): id is number => id != null));
        return this.fieldWorkers.filter(worker => worker.fieldWorkerId != null
            && !selectedElsewhere.has(worker.fieldWorkerId)
            && this.workerCanFillRole(worker, current?.fieldRoleId));
    }

    applyPlaceToLocation(group: FormGroup, context: string, place: google.maps.places.PlaceResult): void {
        const latitude = place.geometry?.location?.lat();
        const longitude = place.geometry?.location?.lng();
        const countryCode = place.address_components?.find(component => component.types.includes('country'))?.short_name;
        const cityName = place.address_components?.find(component =>
            component.types.includes('locality') || component.types.includes('administrative_area_level_1'))?.long_name;
        const country = this.countries.find(option => option.countryCode === countryCode)
            ?? group.get('country')?.value
            ?? null;
        group.patchValue({
            searchLocation: place.formatted_address || place.name || '',
            name: group.get('name')?.value || place.name || place.formatted_address || '',
            placeId: place.place_id || '',
            latitude: Number.isFinite(latitude) ? latitude : null,
            longitude: Number.isFinite(longitude) ? longitude : null,
            country
        }, { emitEvent: false });
        if (!country?.id) {
            return;
        }
        this.baseMasterData.getCities('en-US', country.id).subscribe({
            next: cities => {
                this.citiesByContext[context] = cities ?? [];
                const target = (cities ?? []).find(city =>
                    (city.cityName ?? '').toLocaleLowerCase() === (cityName ?? '').toLocaleLowerCase())
                    ?? (cities ?? []).find(city => (city.cityName ?? '').toLocaleLowerCase()
                        .includes((cityName ?? '').toLocaleLowerCase()));
                if (target) {
                    group.get('city')?.setValue(target, { emitEvent: false });
                }
            },
            error: () => this.citiesByContext[context] = []
        });
    }

    get containerRows(): FormArray {
        return this.form.get('containerLines') as FormArray;
    }

    get containerOptions(): { label: string; value: string }[] {
        return this.containerRows.controls.map(row => String(row.get('containerNumber')?.value ?? '').trim().toUpperCase())
            .filter(number => !!number).map(number => ({ label: number, value: number }));
    }

    private newContainerLine(defaults?: any): FormGroup {
        const row = this.fb.group({ containerNumber: [''], sealNumber: [''], isoEquipmentType: [defaults?.isoEquipmentType ?? null],
            bookingNumber: [defaults?.bookingNumber ?? ''], billOfLadingNumber: [''], houseBillNumber: [''], customsDeclarationNumber: [''] });
        let previousNumber = '';
        row.get('containerNumber')?.valueChanges.subscribe(value => {
            const number = String(value ?? '').trim().toUpperCase();
            if (previousNumber && previousNumber !== number) {
                this.assignmentRows.forEach(assignment => assignment.get('containerNumbers')?.setValue(
                    (assignment.get('containerNumbers')?.value ?? []).filter((selected: string) => selected !== previousNumber)));
            }
            previousNumber = number;
        });
        return row;
    }

    addContainerLine(): void {
        const previous = this.containerRows.at(this.containerRows.length - 1)?.getRawValue();
        this.containerRows.push(this.newContainerLine(previous));
    }

    removeContainerLine(index: number): void {
        if (this.containerRows.length <= 1) return;
        const number = String(this.containerRows.at(index).get('containerNumber')?.value ?? '').trim().toUpperCase();
        this.containerRows.removeAt(index);
        this.assignmentRows.forEach(row => row.get('containerNumbers')?.setValue(
            (row.get('containerNumbers')?.value ?? []).filter((value: string) => value !== number)));
    }

    applySourcePlace(place: google.maps.places.PlaceResult): void {
        this.applyPlaceToLocation(this.form.get('newSourceLocation') as FormGroup, 'source', place);
    }

    setSourceGoogleMapsUrl(url: string): void {
        this.setGoogleMapsUrl(this.form.get('newSourceLocation') as FormGroup, url);
    }

    applyDestinationPlace(place: google.maps.places.PlaceResult): void {
        this.applyPlaceToLocation(this.form.get('newDestinationLocation') as FormGroup, 'destination', place);
    }

    setDestinationGoogleMapsUrl(url: string): void {
        this.setGoogleMapsUrl(this.form.get('newDestinationLocation') as FormGroup, url);
    }

    applyStopPlace(index: number, place: google.maps.places.PlaceResult): void {
        const group = this.intermediateStopsArray.at(index).get('newLocation') as FormGroup;
        this.applyPlaceToLocation(group, `stop-${index}`, place);
    }

    setStopGoogleMapsUrl(index: number, url: string): void {
        const group = this.intermediateStopsArray.at(index).get('newLocation') as FormGroup;
        this.setGoogleMapsUrl(group, url);
    }

    private setGoogleMapsUrl(group: FormGroup, url: string): void {
        group.get('googleMapsUrl')?.setValue(url, { emitEvent: false });
    }

    addIntermediateStop(): void {
        const row = this.newStopRow();
        this.seedLocationDefaults(row.get('newLocation') as FormGroup | null, `stop-${this.intermediateStopsArray.length}`);
        this.intermediateStopsArray.push(row);
    }

    removeIntermediateStop(index: number): void {
        this.intermediateStopsArray.removeAt(index);
    }

    contactsFor(location: Location | null | undefined): Customer[] {
        const contacts = [location?.defaultContact, ...(location?.contacts ?? []), ...this.partyContacts].filter(
            (contact): contact is Customer => !!contact?.id);
        return this.mergeById<Customer>(contacts);
    }

    commercialPartyPhoneCountry(): Country | null {
        const company = this.form.get('company')?.value as Company | null;
        return this.countryById(company?.countryId) ?? this.defaultPhoneCountry();
    }

    currencyCountryId(): number | undefined {
        if (this.form.get('partyMode')?.value === 'new') {
            const country = this.form.get('newClient.addressCountry')?.value as Country | null;
            return country?.id;
        }
        return this.selectedCrmClient?.countryId
            ?? (this.form.get('company')?.value as Company | null)?.countryId
            ?? this.sessionStorage.getCompany()?.countryId;
    }

    controlPhoneCountry(controlPath: string): Country | null {
        const country = this.form.get(controlPath)?.value as Country | null;
        return this.countryById(country?.id) ?? country ?? this.defaultPhoneCountry();
    }

    locationPhoneCountry(location: Location | null | undefined): Country | null {
        return this.countryById(location?.country?.id ?? location?.countryId)
            ?? location?.country
            ?? this.defaultPhoneCountry();
    }

    stopPhoneCountry(row: FormGroup): Country | null {
        if (row.get('mode')?.value !== 'new') {
            return this.locationPhoneCountry(row.get('location')?.value as Location | null);
        }
        const country = row.get('newLocation.country')?.value as Country | null;
        return this.countryById(country?.id) ?? country ?? this.defaultPhoneCountry();
    }

    driverPhoneCountry(row: FormGroup): Country | null {
        const country = row.get('newDriver.residingCountry')?.value as Country | null;
        return this.countryById(country?.id) ?? country ?? this.defaultPhoneCountry();
    }

    onLocationSelected(location: Location | null, target: FormGroup, contactControlName = 'contact'): void {
        if (!location?.id) {
            return;
        }
        this.locationService.getLocationById(location.id).subscribe({
            next: response => {
                const complete = response.data?.[0];
                if (!complete) {
                    return;
                }
                Object.assign(location, complete);
                const contact = target.get(contactControlName);
                if (contact && !contact.value && complete.defaultContact?.id) {
                    contact.setValue(complete.defaultContact, { emitEvent: false });
                }
            }
        });
    }

    onResidenceNumberChange(row: FormGroup): void {
        const driver = row.get('newDriver') as FormGroup;
        const residenceNumber = String(driver?.get('residenceNumber')?.value ?? '').trim();
        const nationality = driver?.get('nationality')?.value as Country | null;
        if (residenceNumber && this.isSaudiCountry(nationality)) {
            driver.get('licenseNumber')?.setValue(residenceNumber, { emitEvent: false });
        }
    }

    onDriverNationalityChange(row: FormGroup): void {
        this.onResidenceNumberChange(row);
    }

    ownershipLabel(value?: string | null): string {
        return this.resourceOwnershipOptions.find(option => option.value === value)?.label ?? 'Owned';
    }

    assignmentOwnershipType(row: FormGroup): ResourceOwnershipType {
        const value = row.getRawValue();
        const driverType = value.driverMode === 'existing'
            ? value.driver?.resourceOwnershipType
            : value.newDriver?.resourceOwnershipType;
        const normalized = String(driverType || 'OWNED').toUpperCase();
        if (normalized === 'TRANSPORTER') return 'TRANSPORTER';
        if (normalized === 'FREELANCE' || normalized === 'FREELANCER' || normalized === 'SUPPLIER') return 'FREELANCE';
        if (normalized === 'RENTAL') return 'RENTAL';
        return 'OWNED';
    }

    assignmentCostLabel(row: FormGroup): string {
        return ['FREELANCE', 'TRANSPORTER'].includes(this.assignmentOwnershipType(row))
            ? 'Buying cost' : 'Internal cost';
    }

    isExternalAssignment(row: FormGroup): boolean {
        return ['FREELANCE', 'TRANSPORTER'].includes(this.assignmentOwnershipType(row));
    }

    vehicleConfigsOf(row: FormGroup): any[] {
        return row.get('newVehicle')?.get('vehicleType')?.value?.vehicleConfigs ?? [];
    }

    vehicleCapacitiesOf(row: FormGroup): any[] {
        return row.get('newVehicle')?.get('vehicleType')?.value?.vehicleCapacities ?? [];
    }

    onVehicleTypeChange(row: FormGroup): void {
        const type = row.get('newVehicle')?.get('vehicleType')?.value;
        row.get('newVehicle')?.patchValue({
            vehicleConfig: type?.vehicleConfigs?.[0] ?? null,
            vehicleCapacity: type?.vehicleCapacities?.[0] ?? null
        });
    }

    onDriverSelect(row: FormGroup, driver: Driver | null): void {
        if (driver?.currentVehicle?.vehicleId && row.get('vehicleMode')?.value === 'existing') {
            const vehicle = this.vehicles.find(option => option.vehicleId === driver.currentVehicle?.vehicleId);
            if (vehicle) {
                row.patchValue({ vehicle }, { emitEvent: false });
            }
        }
    }

    onCommercialPartyChange(reference: CommercialPartyReference): void {
        this.commercialPartyReference = reference;
        this.refreshPricingMatch();
		if (!reference.crmClientId) {
			this.form.patchValue({ paymentTermId: this.paymentTerms[0]?.paymentTermID ?? null });
		}
        this.selectedCrmClient = undefined;
        this.loadWarehouseItems();
        const applyLegacy = (companyId?: number | null, customerId?: number | null): void => {
            this.commercialPartyLegacyCustomerId = customerId ?? undefined;
            const company = this.companies.find(item => item.id === companyId) || null;
            this.form.patchValue({ company });
            this.billingCustomerId = customerId ?? undefined;
            if (company && customerId) {
                delete this.fieldIssues['party'];
            }
        };
        if (reference.source === 'LEGACY') {
            applyLegacy(reference.legacyCompanyId, reference.legacyCustomerId);
            return;
        }
        if (!this.owningCompanyId || !reference.crmClientId) {
            return;
        }
        this.crmService.getClients(this.owningCompanyId, ['CUSTOMER']).subscribe(clients => {
            const client = clients.find(item => item.id === reference.crmClientId);
            this.selectedCrmClient = client;
			this.form.patchValue({ paymentTermId: client?.defaultPaymentTermId ?? this.paymentTerms[0]?.paymentTermID ?? null });
            applyLegacy(client?.linkedCompanyId, client?.linkedCustomerId);
        });
    }

    private refreshPricingMatch(): void {
        if (this.form.get('sellingPriceSource')?.value !== 'RATE_CARD') {
            this.pricingMatchSequence++;
            this.pricingMatchKey = '';
            this.pricingMatch = undefined;
            this.pricingMatchLoading = false;
            return;
        }
        const intake = this.toIntakeRequest();
        const key = JSON.stringify([intake.owningCompanyId, intake.companyId, intake.crmClientId,
            intake.crmAccountId, intake.sourceLocationId, intake.destinationLocationId,
            intake.pickupWindowStart, intake.vehicleTypeId, intake.vehicleConfigId,
            intake.vehicleCapacityId, intake.pricing?.currencyCode]);
        if (key === this.pricingMatchKey) return;
        this.pricingMatchKey = key;
        this.pricingMatch = undefined;
        const sequence = ++this.pricingMatchSequence;
        if (!intake.owningCompanyId || (!intake.companyId && !intake.crmClientId)
            || !intake.sourceLocationId || !intake.destinationLocationId
            || !intake.pickupWindowStart || !intake.vehicleTypeId) {
            this.pricingMatchLoading = false;
            this.pricingMatchUnavailable = 'Select an existing client, route, pickup date, and truck to preview its rate card. New records are matched when the shipment is created.';
            return;
        }
        this.pricingMatchUnavailable = '';
        this.pricingMatchLoading = true;
        this.contractsService.decideRateCard({
            sellingCompanyId: intake.owningCompanyId,
            buyingCompanyId: intake.companyId,
            crmClientId: intake.crmClientId,
            crmAccountId: intake.crmAccountId,
            sourceLocationId: intake.sourceLocationId,
            destinationLocationId: intake.destinationLocationId,
            pickupWindowStart: intake.pickupWindowStart,
            vehicleTypeId: intake.vehicleTypeId,
            vehicleConfigId: intake.vehicleConfigId,
            vehicleCapacityId: intake.vehicleCapacityId,
            currencyCode: intake.pricing?.currencyCode
        }).subscribe({
            next: match => {
                if (sequence !== this.pricingMatchSequence) return;
                this.pricingMatch = match;
                this.pricingMatchLoading = false;
            },
            error: () => {
                if (sequence !== this.pricingMatchSequence) return;
                this.pricingMatchUnavailable = 'Rate card matching is unavailable. Change a selection to retry, or choose Manual.';
                this.pricingMatchLoading = false;
            }
        });
    }

    submit(): void {
        this.fieldIssues = {};
        this.submissionFeedback = null;
        if (!this.validateSubmission()) {
            this.showSubmissionFeedback('error', 'Complete the required shipment details', Object.values(this.fieldIssues));
            this.track('shipment_express_validation', {});
            return;
        }
        this.saving = true;
        this.submissionKey = this.submissionKey ?? this.newIdempotencyKey();
        this.experienceService.quickCreate(this.buildRequest(), this.submissionKey)
            .pipe(finalize(() => this.saving = false))
            .subscribe({
                next: response => this.handleResponse(response),
                error: error => {
                    const body = this.submissionErrorBody(error);
                    const response = body as ShipmentQuickCreateResponse | undefined;
                    if (Array.isArray(response?.issues) && response.issues.length) {
                        this.applyIssues(response);
                        this.showSubmissionFeedback('error', 'Express shipment was not created',
                            response.issues.map(issue => issue.message));
                    } else {
                        const message = typeof body?.message === 'string' && body.message.trim()
                            ? body.message : typeof body?.detail === 'string' && body.detail.trim()
                                ? body.detail : error?.status === 401
                                    ? 'Your session expired. Sign in and try again.'
                                    : error?.status === 0
                                        ? 'Could not reach the server. Check your connection and try again.'
                                        : 'The express shipment could not be saved. Try again.';
                        this.fieldIssues['form'] = message;
                        this.showSubmissionFeedback('error', 'Express shipment was not created', [message]);
                    }
                    this.submissionKey = undefined;
                }
            });
    }

    private handleResponse(response: ShipmentQuickCreateResponse): void {
        this.submissionKey = undefined;
        this.applyIssues(response);
        this.track('shipment_express_result', {
            outcome: response.outcome,
            issue_count: response.issues?.length ?? 0
        });
        if (response.outcome === 'CREATED_AND_ASSIGNED' && response.shipmentId) {
            this.submissionFeedback = null;
            this.completed = true;
            const created = response.createdEntities ?? [];
            if (created.length) {
                this.messages.add({
                    severity: 'info',
                    summary: 'New records created',
                    detail: created.map(entity => this.entityLine(entity)).join(' · ')
                });
            }
            this.messages.add({
                severity: 'success',
                summary: 'Express shipment created',
                detail: `${response.shipmentNumber ?? 'Shipment'} assigned across ${(response.deliveryIds ?? []).length || 1} delivery(ies).`
            });
            this.router.navigate(['/shipment/view', response.shipmentId]);
            return;
        }
        if (response.shipmentId) {
            this.completed = true;
            this.existingShipmentId = response.shipmentId;
            this.showSubmissionFeedback('warning', 'Shipment saved as draft',
                response.issues?.map(issue => issue.message) ?? []);
            return;
        }
        this.showSubmissionFeedback('error', 'Express shipment was not created',
            response.issues?.map(issue => issue.message) ?? []);
    }

    private entityLine(entity: CreatedEntityInfo): string {
        const labels: Record<string, string> = {
            LOCATION: 'Location',
            CLIENT: 'Client',
            CONTACT: 'Contact',
            DRIVER: 'Driver',
            VEHICLE: 'Vehicle',
            LABORER: 'Laborer',
            FIELD_WORKER: 'Field worker'
        };
        const identity = entity.name || entity.code || (entity.id != null ? `#${entity.id}` : '');
        return `${labels[entity.type] ?? entity.type}${identity ? ` ${identity}` : ''}`;
    }

    private applyIssues(response: ShipmentQuickCreateResponse): void {
        response.issues?.forEach(issue => {
            const field = issue.field || 'form';
            const section = field === 'customer' || field === 'parties' ? 'party'
                : field === 'sourceLocation' ? 'source'
                    : field === 'destinationLocation' ? 'destination'
                        : (field === 'vehicle' || field === 'assignment') && this.assignmentRows.length === 1
                            ? 'assignment-0' : field;
            const previous = this.fieldIssues[section];
            this.fieldIssues[section] = previous && previous !== issue.message
                ? `${previous} ${issue.message}` : issue.message;
        });
    }

    private submissionErrorBody(error: any): any {
        const body = error?.error;
        if (typeof body !== 'string') return body;
        try {
            return JSON.parse(body);
        } catch {
            return { message: body };
        }
    }

    private showSubmissionFeedback(tone: 'error' | 'warning', title: string, messages: (string | null | undefined)[]): void {
        const issues = [...new Set(messages.filter((message): message is string =>
            typeof message === 'string' && !!message.trim()).map(message => message.trim()))];
        this.submissionFeedback = {
            tone,
            title,
            issues: issues.length ? issues : [tone === 'warning'
                ? 'Fix the highlighted assignment issues and resubmit to finish dispatch.'
                : 'Review the shipment details and try again.']
        };
    }

    private validateSubmission(): boolean {
        let valid = true;
        const value = this.form.getRawValue();

        for (const controlName of ['pickupWindow', 'quantity']) {
            this.form.get(controlName)?.markAsTouched();
        }
        if (this.form.get('pickupWindow')?.invalid
            || this.form.get('quantity')?.invalid) {
            this.fieldIssues['form'] = 'Complete the highlighted load and schedule fields.';
            valid = false;
        }

        if (value.partyMode === 'existing') {
            if (!this.commercialPartyReference.crmClientId && !this.commercialPartyReference.legacyCompanyId) {
                this.fieldIssues['party'] = 'Select an existing commercial party, or switch to New client.';
                valid = false;
            }
        } else {
            if (!value.newClient?.companyName?.trim() || !value.newClient?.crNumber) {
                this.fieldIssues['party'] = 'Client name and CR number are required to create a new commercial party.';
                valid = false;
            }
        }

        const validateLocation = (mode: string, selected: Location | null, draft: any, key: string, message: string): void => {
            if (mode === 'existing') {
                if (!selected?.id) {
                    this.fieldIssues[key] = message;
                    valid = false;
                }
                return;
            }
            if (!draft?.name?.trim() || !draft?.country?.id || !draft?.city?.id) {
                this.fieldIssues[key] = 'New location needs a name, country, and city.';
                valid = false;
                return;
            }
            const hasMap = !!draft?.placeId || this.isGoogleMapsUrl(draft?.googleMapsUrl);
            const cityCoordinates = draft?.city?.coordinates;
            if ((hasMap && !this.hasValidLocationCoordinates(draft))
                || (!hasMap && (!this.hasValidLocationCoordinates(cityCoordinates)
                    || (Number(cityCoordinates.latitude) === 0 && Number(cityCoordinates.longitude) === 0)))) {
                this.fieldIssues[key] = 'Select a precise map location when valid city coordinates are unavailable.';
                valid = false;
            }
        };
        validateLocation(value.sourceMode, value.sourceLocation, value.newSourceLocation, 'source', 'Select a pickup location or switch to New location.');
        validateLocation(value.destinationMode, value.destinationLocation, value.newDestinationLocation, 'destination', 'Select a drop-off location or switch to New location.');
        this.intermediateStopRows.forEach((row, index) => {
            const stop = row.getRawValue();
            validateLocation(stop.mode, stop.location, stop.newLocation, `stop-${index}`,
                `Select location ${index + 1} or switch to New location.`);
        });

        if (value.cargoSource === 'GENERAL_CARGO') {
            const totalWeight = Number(value.totalWeight);
            if (!value.cargoType?.typeId
                || !value.uom?.uomId
                || !Number.isFinite(totalWeight)
                || totalWeight <= 0
                || !value.weightUnit?.weightId) {
                this.fieldIssues['load'] = 'General cargo needs a cargo type, unit, positive total weight, and weight unit.';
                valid = false;
            }
        }
        if (value.cargoSource === 'WAREHOUSE_ITEM' && !value.warehouseItem?.inventoryItemId) {
            this.fieldIssues['load'] = 'Select a warehouse item.';
            valid = false;
        }
        if (value.cargoSource === 'CONTAINER') {
            const numbers = this.containerOptions.map(option => option.value);
            if (new Set(numbers).size !== numbers.length) {
                this.fieldIssues['load'] = 'Each container number must be unique within the shipment.';
                valid = false;
            }
            const selectedContainers = new Set<string>();
            this.assignmentRows.forEach((row, index) => {
                for (const number of row.get('containerNumbers')?.value ?? []) {
                    if (selectedContainers.has(number)) {
                        this.fieldIssues[`assignment-${index}`] = `Container ${number} is selected for another truck.`;
                        valid = false;
                    }
                    selectedContainers.add(number);
                }
            });
        }
        const pickupIssue = this.outboundPickupIssue();
        if (pickupIssue) {
            this.fieldIssues['load'] = pickupIssue;
            valid = false;
        }

        if (value.sellingPriceSource === 'MANUAL' && (value.sellingPrice == null || value.sellingPrice < 0)) {
            this.fieldIssues['commercials'] = 'Enter a non-negative manual selling price.';
            valid = false;
        }
		if (!value.paymentTermId) {
			this.fieldIssues['commercials'] = 'Select a commercial payment term.';
			valid = false;
		}

        const selectedRoleIds = new Set<number>();
        this.workforceRequirementRows.forEach((row, requirementIndex) => {
            const requirement = row.getRawValue();
            if (!requirement.fieldRoleId) {
                return;
            }
            if (selectedRoleIds.has(requirement.fieldRoleId)) {
                this.fieldIssues['workforce'] = 'Each workforce role can be requested only once.';
                valid = false;
            }
            selectedRoleIds.add(requirement.fieldRoleId);
            if (Number(requirement.headcount) < 1) {
                this.fieldIssues['workforce'] = `Workforce role ${requirementIndex + 1} needs at least one person.`;
                valid = false;
            }
        });

        this.assignmentRows.forEach((row, index) => {
            const rowValue = row.getRawValue();
            if (rowValue.driverMode === 'existing') {
                if (!rowValue.driver?.id) {
                    this.fieldIssues[`assignment-${index}`] = 'Select a driver or switch to New driver for this truck.';
                    valid = false;
                }
            } else if (!rowValue.newDriver?.name?.trim()
                || !rowValue.newDriver?.phoneNumber?.trim()
                || !rowValue.newDriver?.residingCity?.id) {
                this.fieldIssues[`assignment-${index}`] = 'New driver needs a name, phone number, and residing city.';
                valid = false;
            } else if (['TRANSPORTER', 'RENTAL'].includes(rowValue.newDriver?.resourceOwnershipType)
                && !rowValue.newDriver?.transporter?.id) {
                this.fieldIssues[`assignment-${index}`] = 'Select a transporter for the new driver.';
                valid = false;
            }
            if (rowValue.vehicleMode === 'existing') {
                if (!rowValue.vehicle?.vehicleId) {
                    this.fieldIssues[`assignment-${index}`] = (this.fieldIssues[`assignment-${index}`] ? ' ' : '') + 'Select a vehicle or switch to New vehicle for this truck.';
                    valid = false;
                }
            } else if (!rowValue.newVehicle?.plateNumber?.trim()
                || !rowValue.newVehicle?.vehicleType?.id
                || !rowValue.newVehicle?.vehicleConfig?.configId
                || !rowValue.newVehicle?.vehicleCapacity?.capacityId) {
                this.fieldIssues[`assignment-${index}`] = (this.fieldIssues[`assignment-${index}`] ? ' ' : '') + 'New vehicle needs a plate number, type, configuration, and capacity.';
                valid = false;
            } else if (!/^[A-Za-z0-9]+$/.test(rowValue.newVehicle.plateNumber)) {
                this.fieldIssues[`assignment-${index}`] = (this.fieldIssues[`assignment-${index}`] ? ' ' : '')
                    + 'Vehicle plate number can contain only letters and numbers.';
                valid = false;
            } else if (['TRANSPORTER', 'RENTAL'].includes(rowValue.newVehicle?.resourceOwnershipType)
                && !rowValue.newVehicle?.transporter?.id) {
                this.fieldIssues[`assignment-${index}`] = (this.fieldIssues[`assignment-${index}`] ? ' ' : '')
                    + 'Select a transporter for the new vehicle.';
                valid = false;
            }
            if (rowValue.tripMoney?.enabled !== false && rowValue.tripMoney?.cashPurpose !== 'NONE'
                && !this.availableCashBoxes(value.currencyCode || 'SAR')
                    .some(box => box.pettyCashBoxId === rowValue.tripMoney?.pettyCashBoxId)) {
                this.fieldIssues[`assignment-${index}`] = (this.fieldIssues[`assignment-${index}`] ? ' ' : '')
                    + 'Choose an active custodian cash box for driver cash.';
                valid = false;
            }
            if (rowValue.costAmount == null || !Number.isFinite(Number(rowValue.costAmount))
                || Number(rowValue.costAmount) < 0) {
                this.fieldIssues[`assignment-${index}`] = (this.fieldIssues[`assignment-${index}`] ? ' ' : '')
                    + 'Enter a non-negative transport cost for this truck. Zero is allowed.';
                valid = false;
            }
            const selectedWorkers = new Set<number>();
            this.laborSelectionsArray(row).controls.forEach((control, slotIndex) => {
                const selection = control.getRawValue();
                const field = this.workforceFieldKey(index, slotIndex);
                if (selection.mode === 'new') {
                    if (!selection.newLaborer?.name?.trim()
                        || (!selection.newLaborer?.phoneNumber?.trim() && !selection.newLaborer?.emailAddress?.trim())) {
                        this.fieldIssues[field] = 'New laborer needs a name and a mobile number or email address.';
                        valid = false;
                    }
                    return;
                }
                const workerId = selection.fieldWorker?.fieldWorkerId;
                if (workerId == null) {
                    return;
                }
                if (selectedWorkers.has(workerId)) {
                    this.fieldIssues[field] = 'A worker can fill only one labor slot on this delivery.';
                    valid = false;
                }
                selectedWorkers.add(workerId);
                if (!this.workerCanFillRole(selection.fieldWorker, selection.fieldRoleId)) {
                    this.fieldIssues[field] = 'The selected worker is no longer eligible for this labor role.';
                    valid = false;
                }
            });
        });

        return valid;
    }

    private isGoogleMapsUrl(value: unknown): boolean {
        if (typeof value !== 'string' || !value.trim()) {
            return false;
        }
        try {
            const host = new URL(value.trim()).hostname.toLowerCase();
            return ['maps.app.goo.gl', 'www.google.com', 'google.com', 'maps.google.com'].includes(host);
        } catch {
            return false;
        }
    }

    private hasValidLocationCoordinates(draft: any): boolean {
        if (draft?.latitude == null || draft?.latitude === ''
            || draft?.longitude == null || draft?.longitude === '') {
            return false;
        }
        const latitude = Number(draft.latitude);
        const longitude = Number(draft.longitude);
        return Number.isFinite(latitude)
            && Number.isFinite(longitude)
            && latitude >= -90 && latitude <= 90
            && longitude >= -180 && longitude <= 180;
    }

    private buildRequest(): ShipmentQuickCreateRequest {
        const value = this.form.getRawValue();
        const request: ShipmentQuickCreateRequest = {
            intake: this.toIntakeRequest(),
            notes: value.notes || undefined,
            assignments: this.assignmentRows.map(row => {
                const rowValue = row.getRawValue();
                const existingDriver = rowValue.driverMode === 'existing';
                const existingVehicle = rowValue.vehicleMode === 'existing';
                return {
                    driverId: existingDriver ? rowValue.driver?.id : undefined,
                    driverResourceCompanyId: existingDriver ? rowValue.driver?.resourceCompanyId : undefined,
                    vehicleId: existingVehicle ? rowValue.vehicle?.vehicleId : undefined,
                    vehicleResourceCompanyId: existingVehicle ? rowValue.vehicle?.resourceCompanyId : undefined,
                    newDriver: existingDriver ? undefined : {
                        name: rowValue.newDriver.name,
                        phoneNumber: rowValue.newDriver.phoneNumber,
                        emailAddress: undefined,
                        residenceNumber: rowValue.newDriver.residenceNumber || undefined,
                        licenseNumber: rowValue.newDriver.licenseNumber || undefined,
                        nationalityCountryId: rowValue.newDriver.nationality?.id,
                        residingCountryId: rowValue.newDriver.residingCountry?.id,
                        residingCityId: rowValue.newDriver.residingCity?.id,
                        resourceOwnershipType: rowValue.newDriver.resourceOwnershipType,
                        transporterCrmClientId: rowValue.newDriver.transporter?.id
                    },
                    newVehicle: existingVehicle ? undefined : {
                        plateNumber: rowValue.newVehicle.plateNumber,
                        vehicleTypeId: rowValue.newVehicle.vehicleType?.id,
                        vehicleConfigId: rowValue.newVehicle.vehicleConfig?.configId,
                        vehicleCapacityId: rowValue.newVehicle.vehicleCapacity?.capacityId,
                        registrationCountryId: rowValue.newVehicle.registrationCountry?.id,
                        resourceOwnershipType: rowValue.newVehicle.resourceOwnershipType,
                        transporterCrmClientId: rowValue.newVehicle.transporter?.id
                    },
                    laborSelections: this.deliveryLaborSelections(row),
                    containerNumbers: value.cargoSource === 'CONTAINER' ? rowValue.containerNumbers : [],
                    // Finance channel is resolved by the API from the driver,
                    // never from the selected vehicle or a client-side source.
                    transportCostAmount: rowValue.costAmount ?? undefined,
                    transportCostCurrencyCode: value.currencyCode || 'SAR',
                    waybillNumber: rowValue.waybillNumber?.trim() || undefined,
                    tripMoney: {
                        enabled: rowValue.tripMoney?.enabled !== false,
                        applyRateCardDefault: true,
                        payMethod: rowValue.tripMoney?.payMethod || 'NONE',
                        ratePerKm: rowValue.tripMoney?.ratePerKm ?? undefined,
                        distanceSource: rowValue.tripMoney?.distanceSource,
                        expectedStartPlace: rowValue.tripMoney?.expectedStartPlace?.trim() || undefined,
                        expectedParkingPlace: rowValue.tripMoney?.expectedParkingPlace?.trim() || undefined,
                        payTripGroupKey: rowValue.tripMoney?.payTripGroupKey?.trim() || undefined,
                        tripEarningAmount: rowValue.tripMoney?.tripEarningAmount ?? undefined,
                        cashPurpose: rowValue.tripMoney?.cashPurpose || 'NONE',
                        tripCashAmount: rowValue.tripMoney?.tripCashAmount ?? undefined,
                        pettyCashBoxId: rowValue.tripMoney?.cashPurpose === 'NONE' ? undefined
                            : rowValue.tripMoney?.pettyCashBoxId ?? undefined,
                        currencyCode: value.currencyCode || 'SAR',
                        reason: rowValue.tripMoney?.reason?.trim() || undefined
                    }
                };
            })
        };
        if (value.sourceMode === 'new') {
            request.newSourceLocation = this.toInlineLocation(value.newSourceLocation);
        }
        if (value.destinationMode === 'new') {
            request.newDestinationLocation = this.toInlineLocation(value.newDestinationLocation);
        }
        request.sourceContact = this.toLocationContactSelection(
            value.sourceContactMode, value.sourceContact, value.newSourceContact);
        request.destinationContact = this.toLocationContactSelection(
            value.destinationContactMode, value.destinationContact, value.newDestinationContact);
        request.intermediateStops = this.intermediateStopRows.map(row => {
            const stop = row.getRawValue();
            const contact = this.toLocationContactSelection(stop.contactMode, stop.contact, stop.newContact);
            const newLocation = stop.mode === 'new' ? this.toInlineLocation(stop.newLocation) : undefined;
            if (newLocation && contact?.newContact) {
                newLocation.defaultContact = contact.newContact;
            }
            const result: ExpressLocationStop = {
                locationId: stop.mode === 'existing' ? stop.location?.id : undefined,
                newLocation,
                contact: newLocation && contact?.newContact ? undefined : contact,
                remarks: stop.remarks || undefined
            };
            return result;
        });
        if (value.partyMode === 'new') {
            request.newClient = this.toInlineClient(value.newClient);
        } else if (value.addContactForClient && this.commercialPartyReference.crmClientId) {
            const contact: InlineContactDraft = {
                companyId: this.owningCompanyId,
                name: value.contactForClient?.name || undefined,
                emailAddress: value.contactForClient?.email || undefined,
                phoneNumber: value.contactForClient?.phone || undefined
            };
            if (contact.name) {
                request.newContactForClient = contact;
            }
        }
        return request;
    }

    private toInlineLocation(draft: any): InlineLocationDraft {
        const contact = draft?.contactName?.trim()
            ? {
                name: draft.contactName,
                phoneNumber: draft.contactPhone || undefined,
                emailAddress: draft.contactEmail || undefined
            }
            : undefined;
        const latitude = draft?.latitude == null || draft?.latitude === '' ? undefined : Number(draft.latitude);
        const longitude = draft?.longitude == null || draft?.longitude === '' ? undefined : Number(draft.longitude);
        return {
            searchLocation: draft?.searchLocation || undefined,
            googleMapsUrl: this.isGoogleMapsUrl(draft?.googleMapsUrl) ? draft.googleMapsUrl : undefined,
            placeId: draft?.placeId || undefined,
            name: draft?.name,
            countryId: draft?.country?.id,
            cityId: draft?.city?.id,
            locationType: draft?.locationType,
            latitude: Number.isFinite(latitude) ? latitude : undefined,
            longitude: Number.isFinite(longitude) ? longitude : undefined,
            defaultContact: contact
        };
    }

    private toLocationContactSelection(mode: string, contact: Customer | null, draft: any): ExpressLocationContactSelection | undefined {
        if (mode === 'existing') {
            return contact?.id ? { contactId: contact.id } : undefined;
        }
        if (!draft?.name?.trim()) {
            return undefined;
        }
        return {
            newContact: {
                name: draft.name,
                phoneNumber: draft.phone || undefined,
                emailAddress: draft.email || undefined
            }
        };
    }

    private toInlineClient(draft: any): InlineClientDraft {
        const contact = draft?.contactName?.trim()
            ? {
                name: draft.contactName,
                emailAddress: draft.contactEmail || undefined,
                phoneNumber: draft.contactPhone || undefined
            }
            : undefined;
        return {
            companyName: draft?.companyName,
            customerNameLocal: draft?.customerNameLocal || undefined,
            taxRegNumber: draft?.taxRegNumber || undefined,
            crNumber: draft?.crNumber == null ? undefined : Number(draft.crNumber),
            addressStreet1: draft?.addressStreet1 || undefined,
            addressCountryId: draft?.addressCountry?.id,
            addressCityId: draft?.addressCity?.id,
            contact
        };
    }

    private toIntakeRequest(): ShipmentIntakeRequest {
        const value = this.form.getRawValue();
        const company = this.form.value.company as Company | null;
        const source = value.sourceMode === 'existing' ? value.sourceLocation as Location | null : null;
        const destination = value.destinationMode === 'existing' ? value.destinationLocation as Location | null : null;
        const firstRow = this.assignmentRows[0]?.getRawValue();
        const firstVehicle = firstRow?.vehicleMode === 'existing' ? firstRow.vehicle : null;
        const cargoType = this.form.value.cargoType as PayloadType | null;
        const uom = this.form.value.uom as UOM | null;
        const weightUnit = this.form.value.weightUnit as Weight | null;
        const pickupWindow = this.form.value.pickupWindow as Date | null;
        const tenant = this.sessionStorage.getCompany();
        const existingParty = value.partyMode === 'existing';
        const line = value.cargoSource === 'WAREHOUSE_ITEM'
            ? {
                lineNumber: 1,
                lineSource: 'WAREHOUSE_ITEM' as const,
                inventoryItemId: value.warehouseItem?.inventoryItemId,
                warehouseItemNumber: value.warehouseItem?.itemNumber,
                skuNumber: value.warehouseItem?.skuNumber,
                customerItemNumber: value.warehouseItem?.customerItemNumber,
                warehouseItemName: value.warehouseItem?.itemName,
                trackingMode: value.warehouseItem?.trackingMode,
                sourceWarehouseId: value.warehouseFlowDirection === 'INBOUND'
                    ? undefined : value.sourceWarehouseId,
                conditionId: value.warehouseItem?.defaultConditionId,
                description: value.warehouseItem?.itemDescription || value.warehouseItem?.itemName,
                quantity: value.quantity,
                uomId: value.uom?.uomId ?? value.warehouseItem?.defaultUomId,
                totalWeight: value.warehouseItem?.weightKg == null ? undefined
                    : Number(value.warehouseItem.weightKg) * Number(value.quantity),
                weightUnitId: value.weightUnit?.weightId
            }
            : {
                    lineNumber: 1,
                    lineSource: 'GENERAL_CARGO' as const,
                    cargoTypeId: cargoType?.typeId == null ? undefined : Number(cargoType.typeId),
                    description: value.cargoDescription || undefined,
                    quantity: value.quantity,
                    uomId: uom?.uomId,
                    totalWeight: value.totalWeight ?? undefined,
                    weightUnitId: weightUnit?.weightId
                };
        return {
            shipmentId: this.existingShipmentId,
            warehouseFlowDirection: value.cargoSource === 'WAREHOUSE_ITEM'
                ? value.warehouseFlowDirection : 'OUTBOUND',
            externalReference: value.externalReference || undefined,
            companyId: existingParty ? company?.id : undefined,
            customerId: existingParty ? this.billingCustomerId : undefined,
            commercialPartySource: existingParty
                ? (this.commercialPartyReference.source === 'CRM' ? 'CRM' : 'LEGACY') : undefined,
            crmClientId: existingParty ? this.commercialPartyReference.crmClientId ?? undefined : undefined,
            crmAccountId: existingParty ? this.commercialPartyReference.crmAccountId ?? undefined : undefined,
            crmClientContactId: existingParty ? this.commercialPartyReference.crmContactId ?? undefined : undefined,
			paymentTermId: value.paymentTermId ?? undefined,
            legacyCompanyId: existingParty && this.commercialPartyReference.source === 'LEGACY'
                ? this.commercialPartyReference.legacyCompanyId ?? undefined : undefined,
            legacyCustomerId: existingParty && this.commercialPartyReference.source === 'LEGACY'
                ? this.commercialPartyReference.legacyCustomerId ?? undefined : undefined,
            owningCompanyId: tenant?.id,
            owningCompanyContactId: this.sessionStorage.getCustomer()?.id,
            sourceLocationId: source?.id,
            destinationLocationId: destination?.id,
            pickupWindowStart: pickupWindow?.valueOf(),
            requestedTruckCount: this.assignmentsArray.length,
            routeType: 'DOMESTIC',
            serviceCategory: 'FULL_SERVICE',
            vehicleTypeId: firstVehicle?.type?.id ?? firstRow?.newVehicle?.vehicleType?.id,
            vehicleConfigId: firstVehicle?.config?.configId ?? firstRow?.newVehicle?.vehicleConfig?.configId,
            vehicleCapacityId: firstVehicle?.capacity?.capacityId ?? firstRow?.newVehicle?.vehicleCapacity?.capacityId,
            priority: 'NORMAL',
            parties: [
                ...(source?.defaultContact?.id ? [{
                    role: 'SENDER' as const,
                    companyId: source.company?.id,
                    contactId: source.defaultContact.id,
                    self: false,
                    haveAccount: true
                }] : []),
                ...(destination?.defaultContact?.id ? [{
                    role: 'RECEIVER' as const,
                    companyId: destination.company?.id,
                    contactId: destination.defaultContact.id,
                    self: false,
                    haveAccount: true
                }] : [])
            ],
            lines: value.cargoSource === 'CONTAINER'
                ? value.containerLines.map((container: any, index: number) => ({
                    lineNumber: index + 1,
                    lineSource: 'CONTAINER' as const,
                    containerNumber: container.containerNumber?.trim().toUpperCase() || undefined,
                    isoEquipmentType: container.isoEquipmentType || undefined,
                    sealNumber: container.sealNumber?.trim() || undefined,
                    bookingNumber: container.bookingNumber?.trim() || undefined,
                    billOfLadingNumber: container.billOfLadingNumber?.trim() || undefined,
                    houseBillNumber: container.houseBillNumber?.trim() || undefined,
                    customsDeclarationNumber: container.customsDeclarationNumber?.trim() || undefined
                })) : [line],
            workforceRequirements: this.workforceRequirementRows
                .map(row => row.getRawValue())
                .filter(row => !!row.fieldRoleId && Number(row.headcount) > 0)
                .map(row => ({
                    fieldRoleId: row.fieldRoleId,
                    headcount: Number(row.headcount),
                    sellRate: row.sellRate == null ? undefined : Number(row.sellRate),
                    billingUnit: row.sellRate == null ? undefined : row.billingUnit,
                    currencyCode: row.sellRate == null ? undefined : row.currencyCode?.trim()?.toUpperCase()
                }) as ShipmentIntakeLaborRequirement),
            pricing: {
                operationalCostCaptureEnabled: false,
                currencyCode: value.currencyCode || 'SAR',
                rateSource: value.sellingPriceSource || 'MANUAL',
                sellingPrice: value.sellingPriceSource === 'MANUAL' ? value.sellingPrice ?? undefined : undefined
            },
            attachments: []
        };
    }

    private loadWarehouseItems(): void {
        this.warehouseItemsLoading = true;
        this.shipmentIntakeService.searchWarehouseItems({
            crmClientId: this.commercialPartyReference.crmClientId ?? undefined,
            crmAccountId: this.commercialPartyReference.crmAccountId ?? undefined
        }).pipe(finalize(() => this.warehouseItemsLoading = false)).subscribe({
            next: items => this.warehouseItems = items ?? [],
            error: () => this.warehouseItems = []
        });
    }

    private loadTransporterResources(companyId: number, transporters: CrmClient[]): void {
        transporters.forEach(transporter => {
            if (!transporter.id) {
                return;
            }
            this.driverService.getTransporterDrivers(0, 250, companyId, transporter.id).subscribe({
                next: response => this.drivers = this.mergeById(this.drivers, response.items ?? []),
                error: () => undefined
            });
            this.vehicleService.getTransporterVehicles(0, 250, companyId, transporter.id).subscribe({
                next: response => this.vehicles = this.mergeById(this.vehicles, response.items ?? []),
                error: () => undefined
            });
        });
    }

    private mergeById<T extends { id?: number; vehicleId?: number }>(...groups: T[][]): T[] {
        const byId = new Map<number, T>();
        groups.flat().forEach(item => {
            const id = item?.id ?? item?.vehicleId;
            if (id != null) {
                byId.set(id, item);
            }
        });
        return [...byId.values()];
    }

    private isSaudiCountry(country: Country | null | undefined): boolean {
        return country?.countryCode === 'SA' || (country?.name ?? '').toLowerCase().includes('saudi');
    }

    private newClientGroup(): FormGroup {
        return this.fb.group({
            companyName: [''],
            customerNameLocal: [''],
            taxRegNumber: [''],
            crNumber: [null],
            addressStreet1: [''],
            addressCountry: [null],
            addressCity: [null],
            contactName: [''],
            contactPhone: [''],
            contactEmail: ['']
        });
    }

    private newLocationGroup(): FormGroup {
        return this.fb.group({
            name: [''],
            searchLocation: [''],
            googleMapsUrl: [''],
            placeId: [''],
            latitude: [null],
            longitude: [null],
            country: [null],
            city: [null],
            locationType: [null],
            contactName: [''],
            contactPhone: [''],
            contactEmail: ['']
        });
    }

    private newContactGroup(): FormGroup {
        return this.fb.group({
            name: [''],
            phone: [''],
            email: ['']
        });
    }

    private newStopRow(): FormGroup {
        return this.fb.group({
            mode: ['existing' as 'existing' | 'new'],
            location: [null],
            newLocation: this.newLocationGroup(),
            contactMode: ['existing' as 'existing' | 'new'],
            contact: [null],
            newContact: this.newContactGroup(),
            remarks: ['']
        });
    }

    private newWorkforceRequirement(): FormGroup {
        return this.fb.group({
            fieldRoleId: [null],
            headcount: [1, [Validators.required, Validators.min(1)]],
            sellRate: [null],
            billingUnit: ['TASK'],
            currencyCode: ['SAR']
        });
    }

    private newLaborSelection(fieldRoleId: number, slotNumber: number): FormGroup {
        return this.fb.group({
            fieldRoleId: [fieldRoleId],
            slotNumber: [slotNumber],
            mode: ['existing' as 'existing' | 'new'],
            fieldWorker: [null],
            newLaborer: this.fb.group({
                name: [''],
                phoneNumber: [''],
                emailAddress: ['']
            })
        });
    }

    private workforceSlotDefinitions(): Array<{ fieldRoleId: number; slotNumber: number }> {
        return this.workforceRequirementRows.flatMap(row => {
            const requirement = row.getRawValue();
            if (!requirement.fieldRoleId) {
                return [];
            }
            const headcount = Math.max(1, Math.floor(Number(requirement.headcount) || 1));
            return Array.from({ length: headcount }, (_, index) => ({
                fieldRoleId: Number(requirement.fieldRoleId),
                slotNumber: index + 1
            }));
        });
    }

    private workforceSlotKey(fieldRoleId?: number, slotNumber?: number): string {
        return `${fieldRoleId ?? 'none'}:${slotNumber ?? 'none'}`;
    }

    private deliveryLaborSelections(row: FormGroup): DeliveryLaborSelection[] {
        return this.laborSelectionsArray(row).controls.map(control => {
            const selection = control.getRawValue();
            const result: DeliveryLaborSelection = {
                fieldRoleId: selection.fieldRoleId,
                slotNumber: selection.slotNumber
            };
            if (selection.mode === 'existing' && selection.fieldWorker?.fieldWorkerId) {
                result.fieldWorkerId = selection.fieldWorker.fieldWorkerId;
            } else if (selection.mode === 'new' && selection.newLaborer?.name?.trim()) {
                result.newLaborer = {
                    name: selection.newLaborer.name.trim(),
                    phoneNumber: selection.newLaborer.phoneNumber?.trim() || undefined,
                    emailAddress: selection.newLaborer.emailAddress?.trim() || undefined
                } as InlineLaborerDraft;
            }
            return result;
        }).filter(selection => selection.fieldWorkerId != null || selection.newLaborer != null);
    }

    private workerCanFillRole(worker: FieldWorker | null | undefined, fieldRoleId?: number): boolean {
        if (!worker || worker.status !== 'ACTIVE' || !fieldRoleId || !this.isWorkerEffective(worker)) {
            return false;
        }
        const role = this.workforceRoles.find(option => option.fieldRoleId === fieldRoleId);
        if (!role) {
            return false;
        }
        return (role.mandatoryCapabilities ?? []).every(requirement => {
            if (!requirement.fieldCapabilityId) {
                return true;
            }
            return (worker.capabilities ?? []).some(capability => capability.fieldCapabilityId === requirement.fieldCapabilityId
                && capability.status === 'ACTIVE'
                && this.isCapabilityCurrent(capability.expiresOn)
                && Number(capability.proficiencyRank ?? 0) >= Number(requirement.minimumProficiencyRank ?? 0));
        });
    }

    private isWorkerEffective(worker: FieldWorker): boolean {
        const on = this.deliveryDate();
        const starts = this.asDate(worker.effectiveFrom);
        const ends = this.asDate(worker.effectiveUntil);
        return (!starts || starts <= on) && (!ends || ends >= on);
    }

    private isCapabilityCurrent(expiresOn?: string | Date): boolean {
        const expiry = this.asDate(expiresOn);
        return !expiry || expiry >= this.deliveryDate();
    }

    private deliveryDate(): Date {
        return this.asDate(this.form.get('pickupWindow')?.value) ?? new Date();
    }

    private asDate(value?: string | Date | null): Date | undefined {
        if (!value) {
            return undefined;
        }
        const date = value instanceof Date ? value : new Date(value);
        return Number.isNaN(date.getTime()) ? undefined : date;
    }

    private newAssignmentRow(): FormGroup {
        const row = this.fb.group({
            driverMode: ['existing' as 'existing' | 'new'],
            driver: [null],
            newDriver: this.fb.group({
                name: [''],
                phoneNumber: [''],
                residenceNumber: [''],
                licenseNumber: [''],
                nationality: [null],
                residingCountry: [null],
                residingCity: [null, Validators.required],
                resourceOwnershipType: ['OWNED' as ResourceOwnershipType],
                transporter: [null]
            }),
            vehicleMode: ['existing' as 'existing' | 'new'],
            vehicle: [null],
            newVehicle: this.fb.group({
                plateNumber: [''],
                vehicleType: [null],
                vehicleConfig: [null],
                vehicleCapacity: [null],
                registrationCountry: [null],
                resourceOwnershipType: ['OWNED' as ResourceOwnershipType],
                transporter: [null]
            }),
            laborSelections: new FormArray<FormGroup>([]),
            containerNumbers: [[]],
            costAmount: [null, [Validators.required, Validators.min(0)]],
            waybillNumber: [''],
            tripMoney: this.fb.group({
                enabled: [true],
                quotedDriverRate: [null],
                driverRateMessage: [''],
                payMethod: ['NONE'], ratePerKm: [null], distanceSource: ['PHOTO_ODOMETER'],
                expectedStartPlace: [''], expectedParkingPlace: [''], payTripGroupKey: [''],
                tripEarningAmount: [null, Validators.min(0.01)],
                cashPurpose: ['NONE'],
                tripCashAmount: [null, Validators.min(0.01)],
                pettyCashBoxId: [null],
                reason: ['']
            })
        });
        const plateNumber = row.get('newVehicle.plateNumber');
        row.get('driver')?.valueChanges.subscribe(() => this.quoteDriverPay(row));
        row.get('tripMoney.payMethod')?.valueChanges.subscribe(() => this.quoteDriverPay(row));
        plateNumber?.valueChanges.subscribe(value => {
            const normalized = typeof value === 'string' ? value.replace(/\s+/g, '').toUpperCase() : value;
            if (normalized !== value) {
                plateNumber.setValue(normalized, { emitEvent: false });
            }
        });
        return row;
    }

    private quoteDriverPay(row: FormGroup): void {
        const method = row.get('tripMoney.payMethod')?.value;
        const driverId = row.get('driver')?.value?.id;
        const companyId = this.owningCompanyId;
        row.get('tripMoney.quotedDriverRate')?.setValue(null, { emitEvent: false });
        row.get('tripMoney.driverRateMessage')?.setValue('', { emitEvent: false });
        if (!driverId || !companyId || method === 'NONE') return;
        const sourceCityId = this.form.get('sourceMode')?.value === 'existing'
            ? this.form.get('sourceLocation')?.value?.cityId : this.form.get('newSourceLocation.city')?.value?.id;
        const destinationCityId = this.form.get('destinationMode')?.value === 'existing'
            ? this.form.get('destinationLocation')?.value?.cityId : this.form.get('newDestinationLocation.city')?.value?.id;
        this.workforceCash.driverRateQuoteRoute({ owningCompanyId: companyId, driverId, payMethod: method,
            currencyCode: this.form.get('currencyCode')?.value || 'SAR', sourceCityId, destinationCityId,
            customerCrmClientId: this.commercialPartyReference.crmClientId }).subscribe({
            next: quote => { if (row.get('driver')?.value?.id !== driverId || row.get('tripMoney.payMethod')?.value !== method) return;
                row.get('tripMoney.quotedDriverRate')?.setValue(quote.payRate, { emitEvent: false });
                if (method === 'PER_KM' && row.get('tripMoney.ratePerKm')?.value == null)
                    row.get('tripMoney.ratePerKm')?.setValue(quote.payRate); },
            error: error => row.get('tripMoney.driverRateMessage')?.setValue(
                error?.error?.message || 'No matching driver rate. Dispatch can continue; Accounts posting waits for finance.',
                { emitEvent: false })
        });
    }

    private syncAssignmentRows(count: number): void {
        const desired = Math.min(15, Math.max(1, Math.floor(Number.isFinite(count) ? count : 1)));
        const current = this.assignmentsArray.length;
        for (let index = current; index < desired; index++) {
            const row = this.newAssignmentRow();
            this.seedRowCountryDefaults(row, index);
            this.assignmentsArray.push(row);
        }
        for (let index = current - 1; index >= desired; index--) {
            this.assignmentsArray.removeAt(index);
        }
        if (this.form.get('truckCount')?.value !== desired) {
            this.form.get('truckCount')?.setValue(desired, { emitEvent: false });
        }
        this.syncWorkforceSlots();
    }

    private saudiCountry(): Country | null {
        return this.countries.find(country => (country.name || '').toLowerCase().includes('saudi'))
            ?? this.countries[0]
            ?? null;
    }

    defaultPhoneCountry(): Country | null {
        return this.countryById(this.sessionStorage.getCompany()?.countryId) ?? this.saudiCountry();
    }

    private countryById(countryId?: number | null): Country | null {
        return this.countries.find(country => country.id === countryId) ?? null;
    }

    private seedCountryDefaults(): void {
        const country = this.saudiCountry();
        this.seedLocationDefaults(this.form.get('newSourceLocation') as FormGroup | null, 'source');
        this.seedLocationDefaults(this.form.get('newDestinationLocation') as FormGroup | null, 'destination');
        this.form.get('newClient')?.patchValue({ addressCountry: country }, { emitEvent: false });
        if (country?.id) {
            this.countryChange('client-address', country);
        }
        this.seedRowCountryDefaults(this.assignmentRows[0], 0);
    }

    private seedLocationDefaults(group: FormGroup | null, context: string): void {
        if (!group) {
            return;
        }
        const country = this.saudiCountry();
        group.patchValue({ country, locationType: 'CUSTOMER' }, { emitEvent: false });
        if (country?.id) {
            this.countryChange(context, country);
        }
    }

    private seedRowCountryDefaults(row: FormGroup | undefined, index: number): void {
        if (!row) {
            return;
        }
        const country = this.saudiCountry();
        row.get('newDriver')?.patchValue({
            nationality: country,
            residingCountry: country
        }, { emitEvent: false });
        row.get('newVehicle')?.patchValue({ registrationCountry: country }, { emitEvent: false });
        if (country?.id) {
            this.countryChange(`driver-${index}`, country);
            this.countryChange(`vehicle-${index}`, country);
        }
    }

    private newIdempotencyKey(): string {
        return typeof crypto !== 'undefined' && 'randomUUID' in crypto
            ? crypto.randomUUID()
            : `express-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    }

    private track(eventName: string, parameters: Record<string, string | number>): void {
        this.analytics.gtag('event', eventName, parameters);
    }
}
